import type { LayerUnionWithAll } from "@/layers";
import { serializeMapId } from "@/map";
import type { MapId } from "@/types/type-utils";
import type { Viewport } from "@mm/metx-workbench.meteomatics.com";
import { type PayloadAction, createSlice } from "@reduxjs/toolkit";

type SerializedMapAndLayerId = string;

export function serializeMapAndLayerId(id: MapId & { layerId: number }): SerializedMapAndLayerId {
  return [id.map, id.profile, id.tab, id.layerId].join(".");
}

export type ViewportChanges = {
  [mapId: string]: Omit<Viewport, "id_profile" | "id">;
};

export type ActiveLayersPropsChanges = {
  [mapIdAndLayerId: string]: string; // props concat maybe
};

export type AnimationPrefetchFrameStatus = "pending" | "loading" | "ready";

export type AnimationPrefetchMode = "seed_only" | "fill_remaining";

export const DEFAULT_ANIMATION_PREFETCH_INITIAL_FRAMES = 12;

export type AnimationPrefetchProgress = {
  runId: number;
  frameTimestampsMs: number[];
  statusByTimestampMs: { [timestampMs: string]: AnimationPrefetchFrameStatus };
};

export type PrefetchStateType = {
  viewports: ViewportChanges;
  activeLayersProps: ActiveLayersPropsChanges;
  animationPrefetchProgress: AnimationPrefetchProgress | null;
};

const initialState: PrefetchStateType = {
  viewports: {},
  activeLayersProps: {},
  animationPrefetchProgress: null,
};

function normalizeFrameTimestamps(frameTimestampsMs: number[]): number[] {
  /**
   * Progress is keyed by timestamp, so duplicate or non-finite frame entries would
   * make the UI state inconsistent. Normalize once when a new prefetch run starts.
   */
  const unique = Array.from(new Set(frameTimestampsMs.filter((timestampMs) => Number.isFinite(timestampMs))));
  unique.sort((left, right) => left - right);
  return unique;
}

const prefetchStateReducer = createSlice({
  name: "prefetchStates",
  initialState: initialState,
  reducers: {
    setViewportChanges(state, action: PayloadAction<{ id: MapId; viewport: Omit<Viewport, "id_profile" | "id"> }>) {
      if (action.payload.id) {
        state.viewports[serializeMapId(action.payload.id)] = action.payload.viewport;
      }
    },
    setLayerPropsChanges(state, action: PayloadAction<{ id: MapId; layers: LayerUnionWithAll[] }>) {
      if (action.payload.id) {
        for (const layer of action.payload.layers) {
          const id = layer.id;
          state.activeLayersProps[serializeMapAndLayerId({ ...action.payload.id, layerId: id })] =
            JSON.stringify(layer);
        }
      }
    },
    startAnimationPrefetchRun(state, action: PayloadAction<{ runId: number; frameTimestampsMs: number[] }>) {
      const frameTimestampsMs = normalizeFrameTimestamps(action.payload.frameTimestampsMs);
      state.animationPrefetchProgress = {
        runId: action.payload.runId,
        frameTimestampsMs,
        statusByTimestampMs: Object.fromEntries(
          frameTimestampsMs.map((timestampMs) => [String(timestampMs), "pending" as const]),
        ),
      };
    },
    markAnimationPrefetchFramesLoading(state, action: PayloadAction<{ runId: number; frameTimestampsMs: number[] }>) {
      const progress = state.animationPrefetchProgress;
      if (!progress || progress.runId !== action.payload.runId) {
        return;
      }

      for (const timestampMs of action.payload.frameTimestampsMs) {
        const key = String(timestampMs);
        if (!(key in progress.statusByTimestampMs)) {
          continue;
        }

        if (progress.statusByTimestampMs[key] !== "ready") {
          progress.statusByTimestampMs[key] = "loading";
        }
      }
    },
    markAnimationPrefetchFramesReady(state, action: PayloadAction<{ runId: number; frameTimestampsMs: number[] }>) {
      const progress = state.animationPrefetchProgress;
      if (!progress || progress.runId !== action.payload.runId) {
        return;
      }

      for (const timestampMs of action.payload.frameTimestampsMs) {
        const key = String(timestampMs);
        if (!(key in progress.statusByTimestampMs)) {
          continue;
        }
        progress.statusByTimestampMs[key] = "ready";
      }
    },
    resetAnimationPrefetchProgress(state) {
      state.animationPrefetchProgress = null;
    },
  },
});

export const {
  setViewportChanges,
  setLayerPropsChanges,
  startAnimationPrefetchRun,
  markAnimationPrefetchFramesLoading,
  markAnimationPrefetchFramesReady,
  resetAnimationPrefetchProgress,
} = prefetchStateReducer.actions;
export default prefetchStateReducer.reducer;
